import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useEffect, useRef, useState } from "react";
import { FORM_AVAILABLE_STATUSES, StatusAtendimento } from "../constants/statusAtendimento";
import { getLatestAtendimentoStatus } from "../utils/statusUtils";

interface StatusSelectorProps {
  atendimentoId?: number;
  formName?: string; // Nome do formulário para filtrar os status disponíveis
  onStatusChange?: (status: StatusAtendimento) => void;
  onStatusSelect?: (status: StatusAtendimento | null) => void; // Callback para quando o status é selecionado (não salvo ainda)
  value?: string;
}

export const StatusSelector = ({ atendimentoId, formName, onStatusChange, onStatusSelect, value }: StatusSelectorProps) => {
  const [currentStatus, setCurrentStatus] = useState<StatusAtendimento | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<StatusAtendimento | null>(() => {
    // Se value foi fornecido e é válido, usar como valor inicial
    return value && value.trim() !== '' ? (value as StatusAtendimento) : null;
  });
  const [loading, setLoading] = useState(false);
  const isInitialized = useRef(false);
  const userChangedStatus = useRef(false);
  const lastValueRef = useRef<string | undefined>(value);

  // Obter status disponíveis baseado no formulário
  const getAvailableStatuses = (): StatusAtendimento[] => {
    if (!formName || !FORM_AVAILABLE_STATUSES[formName]) {
      // Se não tiver formulário específico, retorna todos os status
      return Object.values(StatusAtendimento);
    }
    return FORM_AVAILABLE_STATUSES[formName];
  };

  // Carregar status inicial quando o componente for montado ou atendimentoId mudar
  useEffect(() => {
    const loadStatus = async () => {
      // Se value foi fornecido na inicialização, usar como valor inicial
      if (value && value.trim() !== '' && !isInitialized.current) {
        const statusValue = value as StatusAtendimento;
        setCurrentStatus(statusValue);
        setSelectedStatus(statusValue);
        isInitialized.current = true;
        return;
      }
      
      if (!atendimentoId || isInitialized.current) {
        return;
      }
      
      // Só carregar do servidor se não tiver um status selecionado localmente
      if (!selectedStatus && !currentStatus) {
        try {
          setLoading(true);
          const status = await getLatestAtendimentoStatus(atendimentoId);
          setCurrentStatus(status);
          setSelectedStatus(status); // Sincronizar também o status selecionado
          isInitialized.current = true;
        } catch (error) {
          console.error('Erro ao carregar status:', error);
          // Em caso de erro, não deixar o select desabilitado permanentemente
          setCurrentStatus(null);
          setSelectedStatus(null);
        } finally {
          setLoading(false);
        }
      }
    };

    loadStatus();
  }, [atendimentoId]); // Só carregar quando atendimentoId mudar

  // Sincronizar com value quando mudar externamente (não quando o usuário seleciona)
  useEffect(() => {
    // Só sincronizar se o value mudou externamente (não foi mudança do usuário)
    if (value && value.trim() !== '' && value !== lastValueRef.current && !userChangedStatus.current) {
      const statusValue = value as StatusAtendimento;
      // Só atualizar se o valor for diferente do atual
      if (statusValue !== selectedStatus) {
        setCurrentStatus(statusValue);
        setSelectedStatus(statusValue);
      }
    }
    // Atualizar a ref do último valor
    lastValueRef.current = value;
    // Resetar a flag de mudança do usuário após processar
    if (userChangedStatus.current) {
      setTimeout(() => {
        userChangedStatus.current = false;
      }, 100);
    }
  }, [value, selectedStatus]);

  const handleStatusChange = async (newStatus: string) => {
    if (!atendimentoId) {
      console.warn('StatusSelector: atendimentoId não fornecido, não é possível atualizar status');
      return;
    }

    const status = newStatus as StatusAtendimento;
    console.log('StatusSelector: Status alterado para:', status, 'atendimentoId:', atendimentoId);
    
    // Marcar que foi uma mudança do usuário
    userChangedStatus.current = true;
    
    // Atualizar imediatamente o estado para mostrar no select
    setCurrentStatus(status);
    setSelectedStatus(status);
    isInitialized.current = true; // Marcar como inicializado para evitar resets
    onStatusSelect?.(status); // Notificar o formulário sobre a seleção
    
    try {
      setLoading(true);
      onStatusChange?.(status);
      
      // Aguardar um pouco para garantir que a API processou e recarregar
      // setTimeout(async () => {
      //   try {
      //     const updatedStatus = await getLatestAtendimentoStatus(atendimentoId);
      //     // Só atualiza se houver um status válido retornado
      //     if (updatedStatus) {
      //       setCurrentStatus(updatedStatus);
      //       setSelectedStatus(updatedStatus);
      //     } else {
      //       // Se retornou null mas acabamos de definir um status, manter o que foi selecionado
      //       setCurrentStatus(status);
      //       setSelectedStatus(status);
      //     }
      //   } catch (error) {
      //     console.error('Erro ao recarregar status:', error);
      //     // Em caso de erro, manter o status selecionado
      //     setCurrentStatus(status);
      //     setSelectedStatus(status);
      //   }
      // }, 300);
      
      toast({
        title: "Status atualizado",
        description: `Status alterado para: ${status}`,
      });
      
      // Disparar evento customizado para atualizar o header
      window.dispatchEvent(new CustomEvent('statusAtendimentoChanged', { 
        detail: { status, atendimentoId } 
      }));
    } catch (error: any) {
      console.error('Erro ao atualizar status:', error);
      // Reverter para o status anterior
      if (currentStatus) {
        setSelectedStatus(currentStatus);
      }
      // Exibir mensagem de erro mais específica
      const errorMessage = error?.message || 'Não foi possível atualizar o status.';
      toast({
        title: "Erro ao atualizar status",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Lista de status disponíveis baseado no formulário
  const availableStatuses = getAvailableStatuses();

  const isDisabled = loading || !atendimentoId;

  return (
    <div className="mb-6 p-4 border rounded-lg bg-card" style={{ position: 'relative', zIndex: 10 }}>
      <Label className="mb-2 block">Status do Atendimento</Label>
      <div style={{ position: 'relative' }}>
        <Select
          value={selectedStatus || currentStatus || StatusAtendimento.NOVO}
          onValueChange={handleStatusChange}
          disabled={isDisabled}
        >
          <SelectTrigger 
            className="w-full"
            style={{ 
              cursor: isDisabled ? 'not-allowed' : 'pointer'
            }}
          >
            <SelectValue placeholder="Selecione o status" />
          </SelectTrigger>
          <SelectContent 
            className="z-[9999]" 
            style={{ zIndex: 9999 }}
            onCloseAutoFocus={(e) => {
              e.preventDefault();
            }}
          >
            {availableStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {loading && (
        <p className="text-xs text-muted-foreground mt-1">Atualizando...</p>
      )}
      {!atendimentoId && (
        <p className="text-xs text-muted-foreground mt-1">ID do atendimento não fornecido</p>
      )}
    </div>
  );
};

